import { create } from 'zustand'
import { ReactNode } from 'react'

export type ToastType = 'success' | 'error' | 'warning' | 'info'

export interface Toast {
  id: string
  type: ToastType
  message: string | ReactNode
  duration?: number
}

interface ToastState {
  toasts: Toast[]
  addToast: (toast: Omit<Toast, 'id'>) => string
  removeToast: (id: string) => void
  clearAll: () => void
}

let toastId = 0

export const useToastStore = create<ToastState>((set, get) => ({
  toasts: [],

  addToast: toast => {
    const id = `toast-${++toastId}`
    const newToast: Toast = {
      ...toast,
      id,
      duration: toast.duration ?? 2000,
    }

    set(state => ({
      toasts: [...state.toasts, newToast],
    }))

    // Auto-remove after duration
    if (newToast.duration && newToast.duration > 0) {
      setTimeout(() => {
        get().removeToast(id)
      }, newToast.duration)
    }

    return id
  },

  removeToast: id => {
    set(state => ({
      toasts: state.toasts.filter(t => t.id !== id),
    }))
  },

  clearAll: () => {
    set({ toasts: [] })
  },
}))

// Convenience functions for direct import
export const toast = {
  success: (message: string | ReactNode, duration?: number) =>
    useToastStore.getState().addToast({ type: 'success', message, duration }),

  error: (message: string | ReactNode, duration?: number) =>
    useToastStore.getState().addToast({ type: 'error', message, duration }),

  warning: (message: string | ReactNode, duration?: number) =>
    useToastStore.getState().addToast({ type: 'warning', message, duration }),

  info: (message: string | ReactNode, duration?: number) =>
    useToastStore.getState().addToast({ type: 'info', message, duration }),

  dismiss: (id: string) => useToastStore.getState().removeToast(id),

  dismissAll: () => useToastStore.getState().clearAll(),
}
